import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eslApi, eslAdminApi } from '@/lib/api-client';

// Electronic shelf labels.
//
// Everything here goes through our own service. The label platform's
// credentials live server-side and are never sent to the browser, which is what
// lets a tenant manage their own hardware without ever holding a vendor login.

export interface EslStore {
  id: string;
  locationId: string;
  storeRef: string;
  timezone: string;
  defaultTemplateId: string | null;
  screenSize: string | null;
  gatewayOnline: number | null;
  gatewayCheckedAt: string | null;
}

export interface EslGateway {
  mac: string;
  locationId: string;
  name: string | null;
  model: string | null;
  firmware: string | null;
  online: number | null;
  lastCheckedAt: string | null;
  offlineSince: string | null;
  registeredBy: string | null;
}

export interface EslLabel {
  mac: string;
  locationId: string;
  itemKey: string | null;
  templateId: string | null;
  battery: number | null;
  rssi: number | null;
  online: number | null;
  boundAt: string | null;
  boundBy: string | null;
}

export interface EslTemplate {
  id: string;
  name: string;
  inch?: number;
  width?: number;
  height?: number;
  colour?: string;
}

export interface EslHealth {
  locationId: string;
  gatewayOnline: boolean;
  gatewayCheckedAt: string | null;
  labelsOutOfDate: number;
  queue: { pending: number; sent: number; dead: number; failed: number; oldest_unconfirmed: string | null };
  tags: { total: number; offline: number; low_battery: number };
}

export interface EslEvent {
  id: number;
  type: string;
  itemKey: string | null;
  mac: string | null;
  actor: string | null;
  actorKind: string | null;
  data: Record<string, unknown>;
  createdAt: string;
}

export interface EslApiClient {
  id: string;
  clientId: string;
  label: string | null;
  scopes: string[];
  locationId: string | null;
  status: string;
  env: 'test' | 'live';
  createdAt: string;
  lastUsedAt: string | null;
  secretRotatedAt: string | null;
}

// Returned ONCE, at creation — a narrower shape than EslApiClient on purpose:
// the create response carries the secret but not the audit fields, so it is not
// an EslApiClient and must not pretend to be one. The secret is stored only as a
// hash, so if it is lost the credential is revoked and reissued — there is no
// recovery.
export interface CreatedEslClient {
  id: string;
  clientId: string;
  clientSecret: string;
  scopes: string[];
  locationId: string | null;
  env: 'test' | 'live';
  warning: string;
}

export function useEslStores() {
  return useQuery({ queryKey: ['esl', 'stores'], queryFn: () => eslApi.get<EslStore[]>('/stores') });
}

// `refresh` asks the platform for live gateway state rather than reading our
// cached copy. A store whose gateway is down stops updating every label in the
// building, so this is worth being able to force.
export function useEslGateways(locationId?: string, refresh = false) {
  return useQuery({
    queryKey: ['esl', 'gateways', locationId, refresh],
    queryFn: () =>
      eslApi.get<EslGateway[]>('/gateways', {
        ...(locationId ? { locationId } : {}),
        ...(refresh ? { refresh: '1' } : {}),
      }),
    enabled: !!locationId,
  });
}

export function useEslLabels(locationId?: string) {
  return useQuery({
    queryKey: ['esl', 'labels', locationId],
    queryFn: () => eslApi.get<EslLabel[]>('/labels', { locationId: locationId! }),
    enabled: !!locationId,
  });
}

export function useEslTemplates(locationId?: string) {
  return useQuery({
    queryKey: ['esl', 'templates', locationId],
    queryFn: () => eslApi.get<EslTemplate[]>('/templates', { locationId: locationId! }),
    enabled: !!locationId,
  });
}

export function useEslHealth(locationId?: string) {
  return useQuery({
    queryKey: ['esl', 'health', locationId],
    queryFn: () => eslApi.get<EslHealth>(`/stores/${locationId}/health`),
    enabled: !!locationId,
    refetchInterval: 30_000,
  });
}

export function useEslEvents(limit = 100) {
  return useQuery({
    queryKey: ['esl', 'events', limit],
    queryFn: () =>
      eslApi.get<EslEvent[]>('/events', {
        since: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(),
        limit,
      }),
  });
}

// Issuing a credential mints a caller that reaches our API and writes to real
// shelves. That is a platform decision, not a tenant's, so these are
// super-admin only and the service refuses them to anyone else — hiding the UI
// alone would leave the endpoint open to a tenant admin, who holds every esl:*
// scope for their own data.
export function useEslApiClients(tenantId?: string) {
  return useQuery({
    queryKey: ['esl', 'api-clients', tenantId],
    queryFn: () => eslAdminApi.get<EslApiClient[]>('/api-clients', tenantId!),
    enabled: !!tenantId,
    retry: false,
  });
}

export function useCreateEslApiClient(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; scopes: string[]; locationId?: string | null; env?: 'test' | 'live' }) =>
      eslAdminApi.post<CreatedEslClient>('/api-clients', tenantId!, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'api-clients'] }),
  });
}

export function useRevokeEslApiClient(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eslAdminApi.delete<unknown>(`/api-clients/${id}`, tenantId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'api-clients'] }),
  });
}

export function useRegisterGateway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locationId: string; mac: string; name?: string }) => eslApi.post('/gateways', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'gateways'] }),
  });
}

export function useImportLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locationId: string; macs: string[] }) => eslApi.post('/labels/import', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'labels'] }),
  });
}

export function useBindLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mac: string; locationId: string; itemKey: string; templateId?: string }) =>
      eslApi.post(`/labels/${input.mac}/bind`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'labels'] }),
  });
}

// Lights the label's LED so staff can find it on the shelf. Worth using before
// binding: linking the wrong label to a product is the one failure the system
// cannot detect for you.
export function useFlashLabel() {
  return useMutation({
    mutationFn: (input: { mac: string; locationId: string }) =>
      eslApi.post(`/labels/${input.mac}/flash`, { locationId: input.locationId, colour: 1 }),
  });
}

export function useOnboardStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      locationId: string;
      storeRef: string;
      timezone?: string;
      gatewayMac?: string;
      tagMacs?: string[];
    }) =>
      eslApi.post<{ locationId: string; steps: Array<{ step: string; ok: boolean; detail?: string }> }>(
        '/onboarding/store',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl'] }),
  });
}

// ── Vendor connection (super admin) ────────────────────────────────────────
//
// The label-platform credentials are WRITE-ONLY. Nothing here returns them, not
// even masked: the API has no endpoint that can, by design. What an operator
// needs to know is whether a tenant is connected, to which host, and whether
// the stored secret still works — and all three are answerable without putting
// a credential back on the wire.

export interface EslTenantConfigStatus {
  configured: boolean;
  // 'platform' = running on the shared account held in the service's secrets,
  // which is the normal case and needs nothing entered here. 'tenant' = this
  // business brought its own. 'none' = neither, so nothing can reach the
  // label platform.
  credentialSource?: 'platform' | 'tenant' | 'none';
  encryptionConfigured: boolean;
  provider?: string;
  baseUrl?: string;
  enabled?: boolean;
  maxItemsPerCall?: number | null;
  minCallIntervalMs?: number | null;
  updatedAt?: string;
  createdAt?: string;
}

export function useEslTenantConfig(tenantId?: string) {
  return useQuery({
    queryKey: ['esl', 'tenant-config', tenantId],
    queryFn: () => eslAdminApi.get<EslTenantConfigStatus>('/tenant-config', tenantId!),
    enabled: !!tenantId,
    retry: false,
  });
}

export function useSaveEslTenantConfig(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    // username/password omitted = keep whatever is stored. That is what makes
    // editing the base URL possible without re-typing a secret nobody can read.
    mutationFn: (input: { provider?: 'minew' | 'mock'; baseUrl?: string; username?: string; password?: string }) =>
      eslAdminApi.put<{ ok: boolean; credentialsChanged: boolean }>('/tenant-config', tenantId!, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'tenant-config'] }),
  });
}

// The only honest way to check a write-only secret is to use it.
export function useTestEslConnection(tenantId?: string) {
  return useMutation({
    mutationFn: () =>
      eslAdminApi.post<{ ok: boolean; baseUrl?: string; error?: string }>('/tenant-config/test', tenantId!),
  });
}

export function useSaveEslStore(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locationId: string; storeRef: string; timezone?: string }) =>
      eslAdminApi.put<{ ok: boolean }>(`/stores/${input.locationId}`, tenantId!, {
        storeRef: input.storeRef,
        timezone: input.timezone ?? 'America/New_York',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl'] }),
  });
}

// Creates the store ON the label platform and records the mapping in one call.
//
// The alternative — listing the platform's stores and picking one — cannot be
// offered: the platform account is shared, so that list contains every tenant's
// store and showing it would leak one business's estate to another. Nothing in
// this portal ever reads that list.
export function useProvisionEslStore(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locationId: string; name: string; address: string; timezone?: string }) =>
      eslAdminApi.post<{ locationId: string; storeRef: string; name: string; adopted: boolean }>(
        `/stores/${input.locationId}/provision`,
        tenantId!,
        { name: input.name, address: input.address, timezone: input.timezone ?? 'America/New_York' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl'] }),
  });
}

export function useEslAdminStores(tenantId?: string) {
  return useQuery({
    queryKey: ['esl', 'admin-stores', tenantId],
    queryFn: () => eslAdminApi.get<EslStore[]>('/stores', tenantId!),
    enabled: !!tenantId,
    retry: false,
  });
}

// ── Test price + credential rotation ───────────────────────────────────────

// Sends one item through the real pipeline: queue -> pacing -> vendor -> poller.
// It is the same endpoint an external POS calls, so a shelf that changes here
// proves the whole Flow B path, not a shortcut around it.
export function usePushEslItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locationId: string; itemKey: string; fields: Record<string, string> }) =>
      eslApi.post<{ accepted: number; skipped: number }>(
        '/items',
        {
          locationId: input.locationId,
          items: [{ itemKey: input.itemKey, fields: input.fields }],
        },
        // POST /items requires an idempotency key so a retried request replays
        // rather than enqueuing the same price twice. A fresh key per send is
        // right here: each press of Send is a new intent, not a retry.
        { 'Idempotency-Key': crypto.randomUUID() },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl'] }),
  });
}

// Changes the secret without changing the client_id, so a partner updates one
// value instead of two and there is no window where neither works.
export function useRotateEslApiClient(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eslAdminApi.post<CreatedEslClient>(`/api-clients/${id}/rotate`, tenantId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'api-clients'] }),
  });
}

// ── Bulk operations ────────────────────────────────────────────────────────
//
// At a thousand labels nobody links one dialog at a time, and nothing is
// all-or-nothing: a bad row in the middle must not lose the rest. The endpoint
// reports per row, so the UI can show exactly which ones need attention.

export interface BulkBindResult {
  bound: number;
  failed: number;
  results: Array<{ mac: string; bound: boolean; error?: string }>;
}

export function useBulkBindLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      locationId: string;
      rows: Array<{ mac: string; itemKeys: string[] }>;
      templateId?: string;
    }) => eslApi.post<BulkBindResult>('/labels/bulk-bind', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esl', 'labels'] }),
  });
}

/** Downloads the CSV that bulk-bind accepts back, so a spreadsheet round-trips. */
export async function downloadEslLabelsCsv(locationId: string) {
  const res = await fetch(`/esl-api/v1/labels/export?locationId=${encodeURIComponent(locationId)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shelf-labels-${locationId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
